<template>
  <div class="min-h-screen bg-canvas flex items-center justify-center py-10 px-5">
    <div class="w-full max-w-[420px]">
      <loader v-if="isLoading"></loader>
      <div v-else class="bg-white border border-border rounded-[20px] px-9 pt-10 pb-8">
        <div class="text-center mb-8">
          <img src="/static/images/unicorn.png" alt="Unicorn Visa" width="48" class="rounded-full mb-5 mx-auto block">
          <h1 class="text-[22px] font-bold text-ink tracking-[-0.02em] m-0 mb-1.5">Create account</h1>
          <p class="text-sm text-muted m-0">Get started with Unicorn Visa</p>
        </div>

        <validation-observer ref="observer" v-slot="{ handleSubmit }">
          <form @submit.stop.prevent="handleSubmit(register)" class="flex flex-col gap-4">
            <div class="flex gap-3">
              <validation-provider
                name="First name"
                :rules="{ required: true, min: 2, alpha: true }"
                v-slot="validationContext"
                class="flex-1 min-w-0"
              >
                <div>
                  <label class="text-[13px] font-semibold text-ink mb-1.5 block" for="firstName">First name</label>
                  <input id="firstName"
                    type="text"
                    class="w-full h-11 border border-border-hover rounded-xl text-sm text-ink px-3.5 transition-[border-color,box-shadow] duration-150 bg-[#fafafa] box-border focus:border-primary focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)] focus:bg-white placeholder:text-placeholder"
                    v-model="form.firstName"
                    placeholder="Jane"
                    :class="{ '!border-[#e53935]': getValidationState(validationContext) === false }">
                  <span v-if="validationContext.errors[0]" class="block text-xs text-[#e53935] mt-1">{{ validationContext.errors[0] }}</span>
                </div>
              </validation-provider>

              <validation-provider
                name="Last name"
                :rules="{ required: true, min: 2, alpha: true }"
                v-slot="validationContext"
                class="flex-1 min-w-0"
              >
                <div>
                  <label class="text-[13px] font-semibold text-ink mb-1.5 block" for="lastName">Last name</label>
                  <input id="lastName"
                    type="text"
                    class="w-full h-11 border border-border-hover rounded-xl text-sm text-ink px-3.5 transition-[border-color,box-shadow] duration-150 bg-[#fafafa] box-border focus:border-primary focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)] focus:bg-white placeholder:text-placeholder"
                    v-model="form.lastName"
                    placeholder="Doe"
                    :class="{ '!border-[#e53935]': getValidationState(validationContext) === false }">
                  <span v-if="validationContext.errors[0]" class="block text-xs text-[#e53935] mt-1">{{ validationContext.errors[0] }}</span>
                </div>
              </validation-provider>
            </div>

            <validation-provider
              name="E-mail"
              :rules="{ email: true, required: true }"
              v-slot="validationContext"
            >
              <div>
                <label class="text-[13px] font-semibold text-ink mb-1.5 block" for="email">Email</label>
                <input id="email"
                  type="email"
                  class="w-full h-11 border border-border-hover rounded-xl text-sm text-ink px-3.5 transition-[border-color,box-shadow] duration-150 bg-[#fafafa] box-border focus:border-primary focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)] focus:bg-white placeholder:text-placeholder"
                  v-model="form.email"
                  placeholder="you@company.com"
                  :class="{ '!border-[#e53935]': getValidationState(validationContext) === false }">
                <span v-if="validationContext.errors[0]" class="block text-xs text-[#e53935] mt-1">{{ validationContext.errors[0] }}</span>
              </div>
            </validation-provider>

            <validation-provider
              name="Password"
              :rules="{required: true, min: 6 }"
              v-slot="validationContext"
            >
              <div>
                <label class="text-[13px] font-semibold text-ink mb-1.5 block" for="password">Password</label>
                <input id="password"
                  type="password"
                  class="w-full h-11 border border-border-hover rounded-xl text-sm text-ink px-3.5 transition-[border-color,box-shadow] duration-150 bg-[#fafafa] box-border focus:border-primary focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)] focus:bg-white placeholder:text-placeholder"
                  v-model="form.password"
                  placeholder="At least 6 characters"
                  :class="{ '!border-[#e53935]': getValidationState(validationContext) === false }">
                <span v-if="validationContext.errors[0]" class="block text-xs text-[#e53935] mt-1">{{ validationContext.errors[0] }}</span>
              </div>
            </validation-provider>

            <div v-if="hasError" class="bg-[#fff5f5] border border-[rgba(255,69,61,0.2)] rounded-xl py-2.5 px-3.5 mb-4">
              <p class="m-0 text-[13px] text-[#e53935] font-medium">Registration failed. Please try again.</p>
            </div>

            <button type="submit" class="w-full h-11 border-0 rounded-full text-sm font-semibold cursor-pointer transition-[opacity,box-shadow] duration-150 flex items-center justify-center gap-2 bg-ink text-white hover:bg-[#2a2b2d] hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] disabled:opacity-60 disabled:cursor-not-allowed" :disabled="isFormLoading">
              Create account
            </button>

            <div class="flex items-center my-1">
              <div class="flex-1 h-px bg-border"></div>
              <span class="px-3.5 text-xs text-placeholder uppercase tracking-[0.06em] font-medium">or</span>
              <div class="flex-1 h-px bg-border"></div>
            </div>

            <button type="button" class="w-full h-11 rounded-full text-sm font-semibold cursor-pointer transition-[opacity,box-shadow] duration-150 flex items-center justify-center gap-2 bg-white text-ink border border-border-hover hover:border-[#d0ccd5] hover:shadow-[0_1px_4px_rgba(0,0,0,0.06)]" @click.prevent="googleSignIn">
              <img src="/static/images/icons/google.png" width="18" alt="">
              Continue with Google
            </button>
          </form>
        </validation-observer>

        <div class="text-center mt-6 pt-5 border-t border-border">
          <p class="m-0 text-sm text-muted">Already have an account? <router-link :to="{ name: 'login' }" class="text-primary font-semibold no-underline hover:opacity-80 hover:no-underline">Sign in</router-link></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
export default {
  name: 'register',
  data () {
    return {
      isLoading: null,
      hasError: false,
      errorsInfo: {
        firstName: null,
        lastName: '',
        email: null,
        password: null
      },
      form: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        onboardingComplete: false
      }
    }
  },
  computed: {
    ...mapState({
      isFormLoading: state => state.auth.loading
    })
  },
  beforeCreate () {
    if (this.$store.state.isLoggedIn) {
      this.$router.push({ name: 'home' })
    }
  },
  methods: {
    googleSignIn () {
      this.$store.dispatch('GOOGLE_SIGN_IN').then((response) => {
        this.$router.push({ name: 'admin-applicants' })
      }).catch(error => {
        console.error('Google sign-in failed:', error)
        this.hasError = true
      })
    },
    getValidationState ({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
    register () {
      const { form } = this
      this.hasError = false
      this.$store.dispatch('REGISTER', form).then((response) => {
        this.$router.push({ name: 'admin-applicants' })
      }).catch(error => {
        console.error('Registration failed:', error)
        this.hasError = true
      })
    }
  },
  mounted () {
    this.isLoading = true
    this.$store.dispatch('SET_LOADING', true)
    setTimeout(() => {
      this.isLoading = false
      this.$store.dispatch('SET_LOADING', false)
    }, 300)
  }
}
</script>
